import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, Search } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Button from "../../components/Button/Button";
import Input from "../Input/Input";
import { TableSkeleton } from "../../components/Skeleton/StatsSkeleton.jsx";

/* ===================== SERVICES ===================== */
import { apiGet } from "../../apiService";

/* ===================== STYLES ===================== */
import styles from "./SidebarMenu.module.scss";

/**
 * Sidebar Component
 * -----------------
 * Displays API categories with nested API list.
 * Supports:
 * - Client-side search
 * - Auto-select first API on load
 * - Category expand/collapse
 *
 * Props:
 * - onItemClick(api): Callback triggered when an API is selected
 */
const SidebarMenu = ({ onItemClick }) => {
  const [categories, setCategories] = useState([]);
  const [openCategory, setOpenCategory] = useState({});
  const [activeItem, setActiveItem] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await apiGet("catalog", "/apimaster/categories-with-apis");
      setCategories(res.data || []);
    } catch (error) {
      console.error("Category API error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (categories.length > 0) {
      const firstCategory = categories[0];
      const firstApi = firstCategory?.apis?.[0];

      if (firstApi) {
        setActiveItem(firstApi.name);
        setOpenCategory({ [firstCategory.categoryId]: true });
        onItemClick && onItemClick(firstApi);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories]);

  const toggleCategory = (categoryId) => {
    setOpenCategory((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const handleItemClick = (api, categoryId) => {
    setActiveItem(api.name);
    setOpenCategory({ [categoryId]: true });
    onItemClick && onItemClick(api);
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
  };

  const filteredCategories = useMemo(() => {
    if (!searchTerm.trim()) return categories;

    return categories
      .map((category) => {
        const filteredApis = category.apis.filter((api) =>
          api.name.toLowerCase().includes(searchTerm.toLowerCase()),
        );

        if (filteredApis.length === 0) return null;

        return {
          ...category,
          apis: filteredApis,
        };
      })
      .filter(Boolean);
  }, [searchTerm, categories]);

  return (
    <div className={styles["admin-ac-sidebar"]}>
      <h3 className={styles["admin-ac-sidebar__title"]}>API Categories</h3>

      <div className={styles["admin-ac-sidebar__search-box"]}>
        <Input
          type="search"
          placeholder="Search APIs..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
        />
        <Button
          text="Search"
          icon={Search}
          className={styles["admin-ac-sidebar__search-btn"]}
          onClick={handleSearch}
        />
      </div>

      <ul className={styles["admin-ac-sidebar__menu"]}>
        {loading ? (
          <TableSkeleton columns={2} rows={3} />
        ) : filteredCategories.length === 0 ? (
          <li className={styles["admin-ac-sidebar__no-data"]}>No APIs found</li>
        ) : (
          filteredCategories.map((category) => {
            const isOpen = openCategory[category.categoryId];

            return (
              <li
                key={category.categoryId}
                className={styles["admin-ac-sidebar__category"]}
              >
                {/* Category Header */}
                <div
                  className={styles["admin-ac-sidebar__category-header"]}
                  onClick={() => toggleCategory(category.categoryId)}
                  role="button"
                  aria-expanded={isOpen}
                >
                  <div className={styles["admin-ac-sidebar__category-title"]}>
                    <span>{category.categoryName}</span>
                    <span className={styles["admin-ac-sidebar__badge"]}>
                      {category.apis.length}
                    </span>
                  </div>
                  <ChevronDown
                    size={16}
                    strokeWidth={2.5}
                    className={`${styles["admin-ac-sidebar__chevron"]} ${
                      isOpen ? styles["admin-ac-sidebar__chevron--open"] : ""
                    }`}
                  />
                </div>

                {/* Nested API List */}
                {isOpen && (
                  <ul className={styles["admin-ac-sidebar__api-list"]}>
                    {category.apis.map((api) => {
                      const isActive = activeItem === api.name;
                      return (
                        <li
                          key={api.id}
                          className={`${styles["admin-ac-sidebar__api-item"]} ${
                            isActive
                              ? styles["admin-ac-sidebar__api-item--active"]
                              : ""
                          }`}
                          onClick={() =>
                            handleItemClick(api, category.categoryId)
                          }
                        >
                          {api.name}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
};

export default SidebarMenu;
